import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../components/ui/VehicleServiceHero';
import { CheckCircle, AlertTriangle, Settings, Target, RotateCw, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';

const WheelService = () => {
  const wheelFeatures = [
    'Precision Alignment',
    'Hunter HawkEye System',
    'Run-Flat Experts',
    'TPMS Service',
    'Performance Fitment',
    'Road Force Balancing'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const warningSignals = [
    'Uneven Tire Wear: Inside or outside edge wear patterns',
    'Vehicle Pulling: Car drifts left or right while driving straight',
    'Steering Wheel Off-Center: Wheel not centered when driving straight',
    'Vibration at Speed: Steering wheel or seat vibration at highway speeds',
    'Squealing Tires: Noise during turns or lane changes',
    'Poor Handling: Vehicle feels loose or unstable',
    'Rapid Tire Wear: Tires wearing faster than expected',
    'Steering Wheel Return: Wheel doesn\'t return to center after turns'
  ];

  const services = [
    {
      title: 'Wheel Alignment Services',
      icon: Target,
      items: [
        '4-wheel computerized alignment',
        'Performance alignment settings',
        'Hunter HawkEye alignment system',
        'Thrust angle correction',
        'Camber, caster, and toe adjustment'
      ]
    },
    {
      title: 'Tire Services',
      icon: RotateCw,
      items: [
        'Tire mounting and balancing',
        'Run-flat tire expertise',
        'TPMS sensor service',
        'Tire rotation and inspection',
        'Road force balancing'
      ]
    },
    {
      title: 'Wheel Repair & Maintenance',
      icon: Settings,
      items: [
        'Alloy wheel repair and refinishing',
        'Wheel balancing and truing',
        'Wheel hub and bearing service',
        'Custom wheel fitment',
        'Staggered setup expertise'
      ]
    },
    {
      title: 'Suspension Services',
      icon: Gauge,
      items: [
        'Suspension diagnostics and repair',
        'Strut and shock replacement',
        'Spring and coilover installation',
        'Suspension geometry correction',
        'Adaptive suspension calibration'
      ]
    }
  ];

  const vehicleServices = [
    {
      brand: 'BMW',
      model: '3/5/7 Series, X3/X5',
      issues: 'Run-flat tire wear, aggressive camber settings, xDrive alignment',
      solution: 'Precision alignment with BMW-specific specifications'
    },
    {
      brand: 'Mercedes-Benz',
      model: 'C/E/S-Class, GLC/GLE',
      issues: 'Airmatic suspension, complex multi-link setup, 4MATIC requirements',
      solution: 'Advanced alignment for sophisticated suspension technologies'
    },
    {
      brand: 'Audi',
      model: 'A4/A6/Q5/Q7',
      issues: 'AWD tire wear patterns, quattro alignment, adaptive suspension',
      solution: 'Specialized quattro all-wheel-drive alignment'
    },
    {
      brand: 'Porsche',
      model: '911/Boxster/Cayenne',
      issues: 'Performance alignment settings, track geometry, PASM adjustments',
      solution: 'Precision alignment for performance-oriented systems'
    },
    {
      brand: 'Land Rover',
      model: 'Discovery/Range Rover',
      issues: 'Air suspension calibration, terrain response, complex geometry',
      solution: 'Advanced alignment for air suspension systems'
    },
    {
      brand: 'Volkswagen',
      model: 'GTI/Golf R/Passat',
      issues: 'Performance tire wear, sport suspension, lowered specs',
      solution: 'Sport-tuned alignment for enhanced handling'
    }
  ];

  const balancingInfo = {
    title: 'What is Tire Balancing?',
    description: 'Tire balancing corrects weight imbalances in your wheel and tire assembly. Even small differences in weight distribution cause vibrations that worsen over time, especially in European vehicles with precision-tuned suspensions and low-profile tires.',
    reasons: [
      'Lightweight alloy wheels amplify vibration issues',
      'Low-profile tires provide less cushioning for imbalances',
      'High-performance vehicles are more sensitive to wheel irregularities',
      'Sophisticated suspension systems transmit vibrations directly to the cabin'
    ],
    consequences: [
      'Premature tire wear - Uneven tread wear requiring early replacement',
      'Suspension damage - Excessive vibration damages struts, shocks, and bearings',
      'Steering issues - Vibration in steering wheel affects handling precision',
      'Reduced comfort - Cabin vibration, especially at highway speeds',
      'Higher fuel consumption - Rolling resistance increases with imbalanced wheels'
    ]
  };

  const rotationInfo = {
    title: 'What is Tire Rotation?',
    description: 'Tire rotation involves moving tires to different positions on your vehicle to ensure even wear across all four tires. European vehicles often have specific rotation patterns due to directional tires, staggered wheel sizes, or all-wheel-drive systems.',
    reasons: [
      'Front tires handle steering and often braking loads differently',
      'AWD systems like Audi quattro and BMW xDrive create unique wear patterns',
      'Performance vehicles with aggressive alignment settings wear tires unevenly',
      'European tire compounds are expensive - rotation maximizes their lifespan'
    ],
    consequences: [
      'Uneven tire replacement - Having to replace two tires instead of four',
      'AWD system damage - Significant tread differences can damage differentials',
      'Reduced traction - Worn tires compromise safety in wet conditions',
      'Poor handling - Uneven wear affects vehicle stability and cornering',
      'Voided warranties - Tire manufacturers require regular rotation for warranty coverage'
    ]
  };

  return (
    <>
      <Helmet>
        <title>Wheel Alignment & Tire Service Dallas | European Specialists | Paradise-Performance</title>
        <meta name="description" content="Expert wheel alignment, tire service & balancing for European vehicles in Dallas. Hunter HawkEye system, run-flat tires, TPMS service. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/wheel-and-tire" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Wheel Service Dallas",
            "description": "Expert wheel alignment and tire services for European vehicles",
            "url": "https://paradise-performance.com/services/wheel-and-tire",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Plano", "Irving"],
            "priceRange": "$$"
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Wheel Services"
        brandColor="#2196F3"
        tagline="Wheel Alignment & Tire Service"
        description="Precision wheel alignment, balancing, and tire services for BMW, Mercedes, Audi, Porsche and other European vehicles. Expert wheel repair and maintenance near Dallas."
        features={wheelFeatures}
        heroImage="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance4.webp"
        heroImageAlt="Professional wheel alignment service"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Expert European Wheel Service Dallas</h2>
            <p className="text-lg text-gray-700 mb-4">
              European vehicles feature sophisticated suspension systems with precision-engineered wheel geometries, 
              advanced alignment specifications, and unique tire requirements. Our specialized technicians understand 
              the exacting standards of BMW, Mercedes-Benz, Audi, Porsche, and other premium European vehicles.
            </p>
            <p className="text-lg text-gray-700">
              From BMW's dynamic stability control to Mercedes' active body control systems, our facility is equipped 
              with state-of-the-art alignment machines and the technical expertise to service the most complex European 
              wheel and suspension systems.
            </p>
          </motion.div>

          {/* Tire Balancing Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-blue-50 to-gray-50 p-8 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{balancingInfo.title}</h2>
            <p className="text-gray-700 mb-6">{balancingInfo.description}</p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div>
                <h3 className="text-xl font-bold text-blue-700 mb-4">Why European Vehicles Need Professional Balancing:</h3>
                <ul className="space-y-2">
                  {balancingInfo.reasons.map((reason, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r">
                <h3 className="text-xl font-bold text-yellow-800 mb-4">Consequences of Unbalanced Wheels:</h3>
                <ul className="space-y-2">
                  {balancingInfo.consequences.map((consequence, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      • {consequence}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Tire Rotation Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-50 to-blue-50 p-8 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{rotationInfo.title}</h2>
            <p className="text-gray-700 mb-6">{rotationInfo.description}</p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div>
                <h3 className="text-xl font-bold text-blue-700 mb-4">Why Tire Rotation is Critical:</h3>
                <ul className="space-y-2">
                  {rotationInfo.reasons.map((reason, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r">
                <h3 className="text-xl font-bold text-yellow-800 mb-4">What Happens Without Regular Rotation:</h3>
                <ul className="space-y-2">
                  {rotationInfo.consequences.map((consequence, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      • {consequence}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* TPMS Section */}
          <motion.div {...fadeInUp} className="bg-blue-50 border-2 border-blue-400 rounded-lg p-8 mb-16">
            <h2 className="text-3xl font-bold text-blue-900 mb-4">TPMS Sensor Service</h2>
            <p className="text-gray-700 mb-4">
              Tire Pressure Monitoring System (TPMS) service involves programming, calibrating, and replacing sensors 
              that monitor tire pressure in real-time. European vehicles use sophisticated TPMS systems that integrate 
              with vehicle stability and safety systems.
            </p>
            
            <div className="bg-white rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Why European TPMS Systems Require Professional Service:</h3>
              <ul className="grid md:grid-cols-2 gap-3">
                {[
                  'Complex integration with vehicle computer systems',
                  'Manufacturer-specific programming procedures and tools',
                  'Different sensor types for different wheel/tire combinations',
                  'Precise pressure specifications for optimal performance'
                ].map((item, i) => (
                  <li key={i} className="flex items-center">
                    <Settings className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-bold text-red-800 mb-2">Consequences of Ignoring TPMS Issues:</h4>
              <div className="grid md:grid-cols-2 gap-2 text-sm text-red-700">
                {[
                  'Safety system failures - ESP, ABS may malfunction',
                  'Inspection failures - Required for vehicle inspection',
                  'Tire damage - Under-inflated tires cause sidewall damage',
                  'Reduced fuel economy - Incorrect pressure increases resistance',
                  'Premature tire replacement - Poor pressure monitoring',
                  'Blowout risk - Severely under-inflated tires can fail'
                ].map((item, i) => (
                  <div key={i}>• {item}</div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Image Gallery */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance5.webp"
                  alt="Professional wheel alignment"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-white font-bold text-sm">Hunter HawkEye Alignment</p>
                </div>
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/brakes/ceramic-disc-brake-european-dfw-repair.webp"
                  alt="Performance wheel service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-white font-bold text-sm">Performance Wheel Services</p>
                </div>
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance6.webp"
                  alt="Tire rotation and balancing"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-white font-bold text-sm">Road Force Balancing</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Warning Signs */}
          <motion.div {...fadeInUp} className="bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded-r-lg mb-16">
            <h2 className="text-3xl font-bold text-yellow-900 mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3" />
              Wheel & Alignment Warning Signs
            </h2>
            <p className="text-gray-700 mb-6">Early detection prevents tire wear and handling issues:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {warningSignals.map((signal, index) => (
                <div key={index} className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{signal}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* European Vehicles Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">European Wheel Service Specialists</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our expertise covers the sophisticated wheel and suspension systems found in premium European vehicles, 
              addressing manufacturer-specific alignment settings and wheel technologies:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicleServices.map((vehicle, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-xl font-bold text-blue-700 mb-2">{vehicle.brand}</h3>
                  <p className="text-sm font-semibold text-gray-600 mb-3">{vehicle.model}</p>
                  <div className="mb-3">
                    <span className="text-sm font-semibold text-gray-700">Common Issues:</span>
                    <p className="text-sm text-gray-600 mt-1">{vehicle.issues}</p>
                  </div>
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-700">{vehicle.solution}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-8 mb-16">
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Notice Uneven Tire Wear?</h3>
              <p className="text-xl mb-8">
                Don't wait until your tires are destroyed. Get a precision alignment check today and save 
                hundreds on premature tire replacement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:8173822316" className="inline-block px-8 py-4 bg-white text-blue-700 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                  Call (817) 382-2316
                </a>
                <Link to="/contact" className="inline-block px-8 py-4 bg-blue-800 text-white font-bold rounded-lg hover:bg-blue-900 transition-colors">
                  Book Alignment Check
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Professional Wheel Services</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    {...fadeInUp}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-blue-600"
                  >
                    <div className="flex items-center mb-4">
                      <Icon className="w-8 h-8 text-blue-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Process Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Wheel Service Process</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { num: 1, title: 'Pre-Alignment Inspection', desc: 'Comprehensive suspension and tire wear pattern analysis' },
                { num: 2, title: 'Precision Measurement', desc: 'Advanced laser alignment system for exact specifications' },
                { num: 3, title: 'Component Inspection', desc: 'Detailed examination of suspension components and wear items' },
                { num: 4, title: 'Precision Adjustment', desc: 'Manufacturer-specific alignment settings for optimal performance' },
                { num: 5, title: 'Road Testing', desc: 'Test drive to verify steering response and tracking' },
                { num: 6, title: 'Final Documentation', desc: 'Detailed alignment report with before/after measurements' }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                  className="relative bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="absolute -top-3 left-4 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-bold mb-2 mt-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How often should European vehicles have wheel alignment?</h3>
                <p className="text-gray-700">
                  Most European manufacturers recommend alignment checks every 12,000-15,000 miles or annually. 
                  Performance vehicles and those with low-profile tires may require more frequent alignment services 
                  due to road conditions and driving style.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why do European cars require special alignment procedures?</h3>
                <p className="text-gray-700">
                  European vehicles often feature complex suspension geometries, adaptive systems, and precise handling 
                  characteristics that require manufacturer-specific alignment specifications and specialized equipment 
                  for proper adjustment.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Can you service run-flat tires?</h3>
                <p className="text-gray-700">
                  Yes, we specialize in run-flat tire service for BMW, Mercedes, and MINI vehicles. Run-flat tires 
                  require specific mounting techniques and equipment to prevent damage during installation and balancing.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Do European vehicles need special wheel balancing?</h3>
                <p className="text-gray-700">
                  European vehicles often use lighter wheels and low-profile tires that are more sensitive to imbalance. 
                  Our precision balancing equipment ensures smooth operation at all speeds, particularly important for 
                  high-performance vehicles.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-4">Ready for Expert Wheel Service?</h2>
            <p className="text-xl mb-8 text-gray-300">
              Our expert technicians use manufacturer-specific specifications to ensure your European vehicle 
              gets the precise wheel service it deserves.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8173822316" className="inline-block px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors">
                Call (817) 382-2316
              </a>
              <Link to="/contact" className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                Schedule Service
              </Link>
            </div>
            
            <div className="mt-12">
              <p className="text-gray-400">
                Related services: {' '}
                <Link to="/services/suspension-repair" className="text-blue-400 hover:underline">Suspension Repair</Link>, {' '}
                <Link to="/services/european-brake-repair" className="text-blue-400 hover:underline">Brake Service</Link>, {' '}
                <Link to="/services/european-performance-tuning" className="text-blue-400 hover:underline">Performance Tuning</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default WheelService;
